import { useState, useEffect, useRef } from "react";
import { Link } from "@tanstack/react-router";
import {
  ArrowRight,
  Star,
  Heart,
  Building2,
  Users2,
  Award,
  ShoppingBag,
} from "lucide-react";
import { partners } from "@/constants/partners";
import EventCard from "../events/EventCard";
import { V1BEClient } from "@/clients";
import { IOrganizationEvent } from "@/types/EventTypes";

const Home = () => {
  const [featuredEvents, setFeaturedEvents] = useState<IOrganizationEvent[]>(
    []
  );
  const heroRef = useRef<HTMLElement>(null);
  const [isHeroVisible, setIsHeroVisible] = useState(false);
  const featuredRef = useRef<HTMLElement>(null);
  const [isFeaturedVisible, setIsFeaturedVisible] = useState(false);
  const testimonialsRef = useRef<HTMLElement>(null);
  const [isTestimonialsVisible, setIsTestimonialsVisible] = useState(false);

  useEffect(() => {
    const fetchFeaturedEvents = async () => {
      const today = new Date().toISOString().split("T")[0];
      const events = await V1BEClient.getOrganizationEvents({
        page: 1,
        page_size: 3,
        date_from: today,
        sort_by: "date_from",
        sort_order: "asc",
      });
      setFeaturedEvents(events);
    };

    fetchFeaturedEvents();
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.target === heroRef.current) {
            setIsHeroVisible(entry.isIntersecting);
          }
          if (entry.target === featuredRef.current) {
            setIsFeaturedVisible(entry.isIntersecting);
          }
          if (entry.target === testimonialsRef.current) {
            setIsTestimonialsVisible(entry.isIntersecting);
          }
        });
      },
      { threshold: 0.1 }
    );

    if (heroRef.current) observer.observe(heroRef.current);
    if (featuredRef.current) observer.observe(featuredRef.current);
    if (testimonialsRef.current) observer.observe(testimonialsRef.current);

    return () => {
      if (heroRef.current) observer.unobserve(heroRef.current);
      if (featuredRef.current) observer.unobserve(featuredRef.current);
      if (testimonialsRef.current) observer.unobserve(testimonialsRef.current);
    };
  }, []);

  const stats = [
    { number: "500+", label: "Wedding Suppliers" },
    { number: "50K+", label: "Happy Celebrants" },
    { number: "100+", label: "Exhibits Hosted" },
    { number: "15+", label: "Years Experience" },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section
        ref={heroRef}
        className="relative bg-gradient-to-r from-primary-600 to-primary-700 text-white overflow-hidden md:p-0 p-4 md:pb-0 pb-8"
      >
        <img
          src="/assets/images/banner.png"
          alt="Hero Background"
          className="fixed inset-0 z-0 w-full h-full object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-black opacity-20"></div>
        <div className="relative container mx-auto px-4 py-20 lg:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1
                className={`text-3xl lg:text-6xl font-bold mb-6 leading-tight md:text-left text-center md:mt-0 mt-6 ${
                  isHeroVisible
                    ? "animate-slide-up-fade delay-100"
                    : "opacity-0"
                }`}
              >
                Premier Wedding
                <span className="block text-primary-200">
                  Supplier Exhibits
                </span>
              </h1>
              <p
                className={`text-xl text-primary-100 mb-8 leading-relaxed ${
                  isHeroVisible
                    ? "animate-slide-up-fade delay-300"
                    : "opacity-0"
                }`}
              >
                Where wedding suppliers showcase their finest work and
                celebrants discover the perfect vendors for their special day.
                Experience the most comprehensive wedding supplier exhibits in
                the Philippines with{" "}
                <strong className="text-yellow-300">PhilTrade</strong>.
              </p>
              <div
                className={`flex flex-col sm:flex-row gap-4 ${
                  isHeroVisible
                    ? "animate-slide-up-fade delay-700"
                    : "opacity-0"
                }`}
              >
                <Link
                  to="/events"
                  className="bg-white text-primary-600 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors duration-200 flex items-center justify-center"
                >
                  Browse Exhibits
                  <ArrowRight size={20} className="ml-2" />
                </Link>
                <Link
                  to="/signup"
                  className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-primary-600 transition-colors duration-200 flex items-center justify-center"
                >
                  <Heart size={20} className="mr-2" />
                  Sign up
                </Link>
              </div>
            </div>
            <div className="relative">
              <div
                className={`bg-white bg-opacity-10 backdrop-blur-sm rounded-2xl p-8 ${
                  isHeroVisible ? "animate-scale-in delay-1000" : "opacity-0"
                }`}
              >
                <div className="grid grid-cols-2 gap-4">
                  {stats.map((stat, index) => (
                    <div key={index} className="text-center">
                      <div className="text-3xl font-bold mb-1">
                        {stat.number}
                      </div>
                      <div className="text-primary-200 text-sm">
                        {stat.label}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="relative z-1">
        {/* Upcoming Exhibits Section */}
        <section ref={featuredRef} className="py-16 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2
                className={`text-3xl lg:text-4xl font-bold text-gray-900 mb-4 ${
                  isFeaturedVisible ? "animate-slide-up-fade" : "opacity-0"
                }`}
              >
                Upcoming Exhibits
              </h2>
              <p
                className={`text-lg text-gray-600 max-w-2xl mx-auto ${
                  isFeaturedVisible
                    ? "animate-slide-up-fade delay-300"
                    : "opacity-0"
                }`}
              >
                Discover upcoming exhibits where you can meet top wedding
                suppliers, view their portfolios, and book services for your
                special day.
              </p>
            </div>

            <div className="columns-1 md:columns-2 lg:columns-3 gap-8 space-y-8 mb-12">
              {featuredEvents.map((event, index) => (
                <div
                  key={event.id}
                  className={
                    isFeaturedVisible ? "animate-slide-up-fade" : "opacity-0"
                  }
                  style={{
                    animationDelay: `${index * 200 + 500}ms`,
                  }}
                >
                  <EventCard event={event} />
                </div>
              ))}
            </div>

            <div className="text-center">
              <Link
                to="/events"
                className={`inline-flex items-center bg-gray-900 text-white px-8 py-4 rounded-lg font-semibold hover:bg-gray-800 transition-colors duration-200 ${
                  isFeaturedVisible
                    ? "animate-slide-up-fade delay-1000"
                    : "opacity-0"
                }`}
              >
                View All Exhibits
                <ArrowRight size={20} className="ml-2" />
              </Link>
            </div>
          </div>
        </section>

        {/* Why Choose Us Section */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
                Why Choose PhilTrade Exhibits?
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                The premier platform connecting wedding suppliers with
                celebrants through curated exhibits and professional networking
                opportunities.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {[
                {
                  icon: <Building2 className="w-8 h-8 text-primary-600" />,
                  title: "Supplier Showcases",
                  description:
                    "Wedding suppliers display their finest work and services in professional exhibit settings.",
                },
                {
                  icon: <Users2 className="w-8 h-8 text-primary-600" />,
                  title: "Meet Celebrants",
                  description:
                    "Direct access to engaged couples actively seeking wedding services and suppliers.",
                },
                {
                  icon: <ShoppingBag className="w-8 h-8 text-primary-600" />,
                  title: "Book Services",
                  description:
                    "Celebrants can view portfolios, compare options, and book services directly at exhibits.",
                },
                {
                  icon: <Award className="w-8 h-8 text-primary-600" />,
                  title: "Quality Assurance",
                  description:
                    "Curated exhibits featuring only verified, professional wedding suppliers and vendors.",
                },
              ].map((feature, index) => (
                <div key={index} className="text-center">
                  <div className="flex justify-center mb-4">{feature.icon}</div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials Section */}
        <section ref={testimonialsRef} className="py-16 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2
                className={`text-3xl lg:text-4xl font-bold text-gray-900 mb-4 ${
                  isTestimonialsVisible ? "animate-slide-up-fade" : "opacity-0"
                }`}
              >
                What Our Community Says
              </h2>
              <p
                className={`text-lg text-gray-600 max-w-2xl mx-auto ${
                  isTestimonialsVisible
                    ? "animate-slide-up-fade delay-300"
                    : "opacity-0"
                }`}
              >
                Hear from both wedding suppliers and celebrants about their
                experience with PhilTrade exhibits.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[
                {
                  name: "Maria Santos",
                  role: "Wedding Photographer",
                  company: "Elegant Moments Photography",
                  content:
                    "PhilTrade exhibits have been incredible for my business. I've booked 15 weddings this year alone through their exhibits.",
                  rating: 5,
                },
                {
                  name: "Ana & Carlos Rodriguez",
                  role: "Celebrants",
                  company: "Beach Wedding Couple",
                  content:
                    "We found our perfect venue, photographer, and florist all at one PhilTrade exhibit. Everything was perfect!",
                  rating: 5,
                },
                {
                  name: "Miguel Dela Cruz",
                  role: "Wedding Planner",
                  company: "Dream Weddings PH",
                  content:
                    "The quality of suppliers and the professional setup at PhilTrade exhibits is unmatched. Highly recommended!",
                  rating: 5,
                },
              ].map((testimonial, index) => (
                <div
                  key={index}
                  className={`bg-white rounded-lg shadow-lg p-6 ${
                    isTestimonialsVisible
                      ? "animate-slide-up-fade"
                      : "opacity-0"
                  }`}
                  style={{
                    animationDelay: `${index * 200 + 500}ms`,
                  }}
                >
                  <div className="flex items-center mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star
                        key={i}
                        className="text-yellow-400 fill-current"
                        size={16}
                      />
                    ))}
                  </div>
                  <p className="text-gray-600 mb-4 italic">
                    "{testimonial.content}"
                  </p>
                  <div>
                    <p className="font-semibold text-gray-900">
                      {testimonial.name}
                    </p>
                    <p className="text-sm text-gray-500">
                      {testimonial.role}, {testimonial.company}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Partners */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="text-center mb-10">
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
                Our Partner Suppliers
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                We're proud to work with the finest wedding suppliers in the
                Philippines.
              </p>
            </div>
            <div className="flex flex-wrap justify-center items-center gap-12">
              {partners.map((partner, idx) => {
                const card = (
                  <>
                    <img
                      src={partner.image}
                      alt={partner.name}
                      className="w-auto h-20 mb-2 object-contain"
                    />
                    <h3 className="text-lg font-semibold text-gray-800">
                      {partner.name}
                    </h3>
                  </>
                );
                const cardClassName =
                  "flex flex-col items-center bg-white rounded-xl shadow-md p-6 transition-transform hover:scale-105 hover:shadow-lg";

                if (partner.link) {
                  return (
                    <a
                      key={idx}
                      href={partner.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`${cardClassName} cursor-pointer`}
                    >
                      {card}
                    </a>
                  );
                }

                return (
                  <div key={idx} className={cardClassName}>
                    {card}
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-primary-600 text-white">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl lg:text-4xl font-bold mb-4">
              Ready to Join Our Exhibit Community?
            </h2>
            <p className="text-xl text-primary-100 mb-8 max-w-2xl mx-auto">
              Whether you're a wedding supplier looking to showcase your work or
              a celebrant seeking the perfect vendors, join PhilTrade today and
              be part of the premier wedding supplier exhibit platform.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/signup"
                className="bg-white text-primary-600 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors duration-200"
              >
                Sign up
              </Link>
              <Link
                to="/contact"
                className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-primary-600 transition-colors duration-200"
              >
                Contact Us
              </Link>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Home;
